const units = {
  'min': 'minute',
  'h': 'hour',
  'd': 'day',
  'w': 'week',
  'm': 'month', // order matters, prefixes come later
  'q': 'quarter',
  'y': 'year',
}
const longUnits = Object.values(units)
                  .flatMap(u => [u, `${u}s`])
                  .join('|')
const shortUnits = Object.keys(units).join('|')
const allUnits = `(${longUnits}|${shortUnits})`

const numbers = {
  'one ': 1,
  'two ': 2,
  'three ': 3,
  'four ': 4,
  'five ': 5,
  'six ': 6,
  'seven ': 7,
  'eight ': 8,
  'nine ': 9,
  'ten ': 10,
  'eleven ': 11,
  'twelve ': 12,
}
const extNumbers = {
  ...numbers,
  'an ': 1,
  'a ': 1, // order matters, prefixes come later
  'half ': (unit) => {
    switch (unit) {
      case 'hour': return [30, 'minute']
      case 'day': return [12, 'hour']
      case 'month': return [2, 'week']
      case 'year': return [6, 'month']
      default: return null
    }
  },
}
const amounts = `\\d+|${Object.keys(numbers).join('|')}`
const extAmounts = `\\d+|${Object.keys(extNumbers).join('|')}`
const matchValues = new RegExp(`^(${extAmounts})\\s*(${allUnits}|)`,'i')

export const parseValue = (input) => {
  const match = input.match(matchValues)
  if (!match) {
    return null
  }

  const rAmt = match[1].toLowerCase()
  let amt
  let unit = match[2].toLowerCase()

  // special case: allow for bare numbers to represent days
  if (unit === '') {
    if (!extNumbers[rAmt]) {
      unit = 'day'
    } else {
      return null
    }
  }

  // normalize units
  if (units[unit]) {
    unit = units[unit]
  }
  if (unit.endsWith('s')) {
    unit = unit.slice(0,-1)
  }

  // normalize amounts
  if (extNumbers[rAmt]) {
    if (typeof extNumbers[rAmt] === 'function') {
      const res = extNumbers[rAmt](unit)
      if (!res) {
        return null
      }
      amt = res[0]
      unit = res[1]
    } else {
      amt = extNumbers[rAmt]
    }
  } else {
    amt = parseInt(rAmt, 10)
  }

  return {
    len: match[0].length,
    val: [amt, unit]
  }
}

const offset = {
  '+': 'add',
  'add ': 'add',
  'plus ': 'add',
  '-': 'subtract',
  'subtract ': 'subtract',
  'sub ': 'subtract',
  'minus ': 'add',
  'in ': 'add',
}
const allOffsets = Object.keys(offset).map(k => {
  switch (k) {
    case '+': return '\\+'
    case '-': return '\\-'
    default: return k
  }
}).join('|')
const matchOffset = new RegExp(`(${allOffsets})\\s*`)
export const parseOffset = (input) => {
  const match = input.match(matchOffset)
  if (!match || !offset[match[1]]) {
    return null
  }
  const value = parseValue(input.slice(match.index + match[0].length))
  if (!value) {
    return null
  }
  if (offset[match[1]] === 'subtract') {
    value.val[0] = -value.val[0]
  }
  return {
    idx: match.index,
    len: value.len + match[0].length,
    offset: value.val
  }
}

// TODO: Can probably use a browser api to get this locale aware
const dayOfWeek = {
  'sun': 0,
  'sunday': 0,
  'mon': 1,
  'monday': 1,
  'tue': 2,
  'tues': 2,
  'tuesday': 2,
  'wed': 3,
  'wednesday': 3,
  'thu': 4,
  'thur': 4,
  'thurs': 4,
  'thursday': 4,
  'fri': 5,
  'friday': 5,
  'sat': 6,
  'saturday': 6,
}
const DoW = Object.keys(dayOfWeek).join('|')
const matchStartDoW = new RegExp(`(last |next |^)\\s*(${DoW})\\b\\s*`,'i')

const startRel = {
  'y': [-1, 'day'],
  'yesterday': [-1, 'day'],
  't': [1, 'day'],
  'tomorrow': [1, 'day'],
  'n': [0, 'day'],
  'now': [0, 'day'],
  'today': [0, 'day']
}
const matchStartRel = new RegExp(`^(${Object.keys(startRel).join('|')})\\b\\s*`,'i')
export const parseStart = (input) => {
  let startOffset = [0, 'day']
  let startDayOfWeek
  let idx = 0
  let len = 0

  const matchDoW = input.match(matchStartDoW)
  if (matchDoW) {
    idx = matchDoW.index
    len = matchDoW[0].length
    if (matchDoW[1] === 'last ') {
      startOffset = [-1, 'week']
    } else if (matchDoW[1] === 'next ') {
      startOffset = [1, 'week']
    }
    startDayOfWeek = dayOfWeek[matchDoW[2]]
  } else {
    const matchRel = input.match(matchStartRel)
    if (matchRel) {
      idx = matchRel.index
      len = matchRel[0].length
      startOffset = startRel[matchRel[1].toLowerCase()]
    }
  }
  if (!len) {
    return null
  }
  return {
    idx,
    len,
    startOffset,
    startDayOfWeek
  }
}

export const parseRelativeDate = (input) => {
  const resStart = parseStart(input)
  const offsetText = resStart ? input.slice(resStart.idx + resStart.len) : input
  const resOffset = parseOffset(offsetText)
  if (resStart || resOffset) {
    return {
      idx: resStart?.idx ?? resOffset?.idx ?? 0,
      len: (resStart?.len || 0) + (resOffset?.len || 0),
      startOffset: resStart?.startOffset,
      startDayOfWeek: resStart?.startDayOfWeek,
      offset: resOffset?.offset
    }
  } else {
    return null
  }
}
